import React, { useState } from 'react'
import {
  Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Stack, Switch, TextField, Typography
} from '@mui/material'
import { CheckRounded } from '@mui/icons-material'
import StepCard from './StepCard'

const TITLE_ID = 'camera-control-phone-image-title'
const DELETE_ALL_TITLE_ID = 'camera-control-delete-all-title'

/**
 * Story 213, reopening 5 (C17.7, RN150, RN151, RN155-RN157; design Main.dc.html v7) — whether the phone image of a PHONE
 * incident is saved. "Save phone image" is saved at once (`onSaveImage`), like the status switch: turning it on asks to
 * confirm that the staff is informed — with the SAVED days and blur — and the checkbox is never stored nor sent; turning it
 * off asks nothing. "Blur faces" and the days are pending changes of the page's bar, dimmed while no image is saved.
 * C17.10: "Delete all images" with the number `available`, outside what is dimmed (turning the image off deletes nothing),
 * disabled with 0; it applies at once after its confirmation (`onDeleteAll`).
 */
const PhoneImagesBlock = ({
  imageSaved, savedBlur, savedDays, blur, days, daysInvalid, available, onSaveImage, onBlur, onDays, onDeleteAll, dimmedOpacity, t_
}) => {
  const [confirming, setConfirming] = useState(false)
  const [informed, setInformed] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  const toggle = (checked) => {
    if (!checked) {
      onSaveImage(false)
      return
    }
    setInformed(false)
    setConfirming(true)
  }

  const confirm = () => {
    setConfirming(false)
    onSaveImage(true)
  }

  // RN151: what is saved, for how long, whether faces are blurred and who sees it (hub 2026-10-27: two lines for what and how
  // long).
  const lines = [
    t_("When a phone is detected, a photo is saved with the phone's box and its confidence"),
    t_('It is kept for {{days}} days and then deleted', { days: savedDays }),
    savedBlur ? t_('Faces are blurred') : t_('Faces are not blurred'),
    t_('Only ADMIN and SUPERADMIN can see it, and each view is recorded')
  ]

  return (
    <StepCard id='camera-control-phone-images' title={t_('Phone images')}>
      <Stack direction='row' alignItems='center' spacing={1}>
        <Switch
          checked={imageSaved}
          onChange={(event) => toggle(event.target.checked)}
          inputProps={{ role: 'switch', 'aria-label': t_('Save phone image') }}
        />
        <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>{t_('Save phone image')}</Typography>
      </Stack>
      <Stack
        data-testid='phone-image-options'
        direction='row'
        flexWrap='wrap'
        alignItems='flex-start'
        gap={1.5}
        columnGap={3.5}
        sx={{ pl: 6.5, opacity: imageSaved ? 1 : dimmedOpacity }}
      >
        <Stack direction='row' alignItems='flex-start' spacing={1}>
          <Switch
            checked={blur}
            disabled={!imageSaved}
            onChange={(event) => onBlur(event.target.checked)}
            inputProps={{ role: 'switch', 'aria-label': t_('Blur faces') }}
          />
          <Stack sx={{ pt: 1 }}>
            <Typography variant='body2' sx={{ color: 'text.primary' }}>{t_('Blur faces')}</Typography>
            <Typography variant='caption' sx={{ color: '#A8A8A8' }}>{t_('Only affects new images')}</Typography>
            {/* R-62 (PO 2026-10-27, H-C17-1): the face detector does not find every face. */}
            <Typography variant='caption' sx={{ color: '#FFCC80' }}>{t_('Faces turned sideways or partly hidden may not be detected and stay unblurred')}</Typography>
          </Stack>
        </Stack>
        <TextField
          type='number'
          size='small'
          label={t_('Keep phone images for (days)')}
          value={days}
          error={daysInvalid}
          disabled={!imageSaved}
          onChange={(event) => onDays(event.target.value)}
          inputProps={{ min: 1, max: 90, step: 1, inputMode: 'numeric' }}
          sx={{ width: 240, mt: 0.5 }}
        />
      </Stack>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #555555', pt: 1.75 }}>
        <Button
          variant='outlined'
          color='error'
          disabled={available === 0}
          onClick={() => setConfirmingDelete(true)}
          sx={{ borderRadius: 999 }}
        >
          {t_('Delete all images ({{count}} saved)', { count: available })}
        </Button>
      </Box>

      <Dialog open={confirmingDelete} onClose={() => setConfirmingDelete(false)} aria-labelledby={DELETE_ALL_TITLE_ID} maxWidth='xs' fullWidth>
        <DialogTitle id={DELETE_ALL_TITLE_ID} sx={{ fontWeight: 600 }}>{t_('Delete the {{count}} saved images?', { count: available })}</DialogTitle>
        <DialogContent>
          <Typography variant='body2' sx={{ color: '#CFCFCF' }}>{t_("It can't be undone. The incidents stay.")}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
          <Button variant='outlined' onClick={() => setConfirmingDelete(false)} sx={{ borderRadius: 999 }}>{t_('Cancel')}</Button>
          <Button
            variant='contained'
            color='error'
            onClick={() => {
              setConfirmingDelete(false)
              onDeleteAll()
            }}
            sx={{ borderRadius: 999 }}
          >
            {t_('Delete all')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirming}
        onClose={() => setConfirming(false)}
        aria-labelledby={TITLE_ID}
        PaperProps={{ sx: { maxWidth: 560, bgcolor: '#424242', borderRadius: 3, border: '1px solid #555555', backgroundImage: 'none' } }}
      >
        <DialogTitle id={TITLE_ID} sx={{ fontWeight: 600 }}>{t_('Save phone image?')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Stack component='ul' spacing={1.25} sx={{ m: 0, p: 0, listStyle: 'none' }}>
              {lines.map((line) => (
                <Stack component='li' direction='row' spacing={1.5} key={line} sx={{ color: '#E6E6E6', fontSize: 14, lineHeight: 1.5 }}>
                  <CheckRounded sx={{ color: 'primary.main', fontSize: 20, mt: '1px' }} />
                  <Box component='span' data-testid='phone-image-line'>{line}</Box>
                </Stack>
              ))}
            </Stack>
            <FormControlLabel
              control={<Checkbox checked={informed} onChange={(event) => setInformed(event.target.checked)} />}
              label={t_('I have informed the staff that a photo is saved when a phone is detected')}
              sx={{ m: 0, alignItems: 'flex-start', bgcolor: '#383838', border: '1px solid #FF8F00', borderRadius: 3, p: 1.5, color: 'text.primary' }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
          <Button variant='outlined' onClick={() => setConfirming(false)} sx={{ borderRadius: 999 }}>{t_('Cancel')}</Button>
          <Button variant='contained' disabled={!informed} onClick={confirm} sx={{ borderRadius: 999 }}>{t_('Save images')}</Button>
        </DialogActions>
      </Dialog>
    </StepCard>
  )
}

export default PhoneImagesBlock
